import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ChevronDownOutline, ChevronUpOutline, Flex, Heading, HeadingProps, useDisclosure } from '@bonusly/design-system';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { useHomepageRedesignEnabled } from '../../BonusFeed/hooks/useHomepageRedesignEnabled';
import { useMonthlyInteraction } from '../hooks/use-interaction';
import { Person } from '../types';
import { PersonStats } from './person-stats';

interface Props {
	people: Person[];
}

const COLLAPSED_PEOPLE_TO_DISPLAY = 5;

export const PersonList: FC<Props> = ({ people }) => {
	const { t } = useTranslation('tracker_widget');
	const canAccessAdvice = usePermission(Permission.CanAccessAdvice);
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const showFeedbackFeatures = canAccessAdvice && !isHomepageRedesignEnabled;

	const { isOpen, onToggle } = useDisclosure();

	// Show all people when homepage redesign is enabled (used in team panel)
	const collapsedPeopleCount = isHomepageRedesignEnabled ? undefined : COLLAPSED_PEOPLE_TO_DISPLAY;

	const interactionType = showFeedbackFeatures ? 'any' : 'recognition';
	const { interactionPeopleInMonth, noInteractionPeopleInMonth } = useMonthlyInteraction({ people, type: interactionType });

	const peopleToDisplay = useMemo(() => {
		const ordered = noInteractionPeopleInMonth.concat(interactionPeopleInMonth);
		return new Set(ordered.slice(0, isOpen ? undefined : collapsedPeopleCount));
	}, [interactionPeopleInMonth, noInteractionPeopleInMonth, isOpen, collapsedPeopleCount]);

	const [noInteractionToDisplay, interactionToDisplay] = useMemo(() => {
		const unrecognized = noInteractionPeopleInMonth.filter((person) => peopleToDisplay.has(person));
		const recognized = interactionPeopleInMonth.filter((person) => peopleToDisplay.has(person));
		return [unrecognized, recognized];
	}, [peopleToDisplay, noInteractionPeopleInMonth, interactionPeopleInMonth]);

	if (noInteractionToDisplay.length <= 0 && interactionToDisplay.length <= 0) {
		return null;
	}

	return (
		<Flex alignContent="flex-start" flexDirection="column" gap={4}>
			{noInteractionToDisplay.length > 0 && (
				<StyledHeading>{showFeedbackFeatures ? t('person_list.havent_interacted') : t('person_list.havent_recognized')}</StyledHeading>
			)}
			{noInteractionToDisplay.map((person, index) => {
				return <PersonStats person={person} key={`no-interaction-stat-${index}`} />;
			})}

			{interactionToDisplay.length > 0 && <StyledHeading>{showFeedbackFeatures ? t('person_list.interacted') : t('person_list.recognized')}</StyledHeading>}

			{interactionToDisplay.map((person, index) => {
				return <PersonStats person={person} key={`interaction-stat-${index}`} />;
			})}

			{collapsedPeopleCount && noInteractionPeopleInMonth.length + interactionPeopleInMonth.length > collapsedPeopleCount && (
				<Button
					onClick={onToggle}
					rightIcon={isOpen ? <ChevronUpOutline size="xs" /> : <ChevronDownOutline size="xs" />}
					colorScheme="secondary"
					variant="ghost"
					size="xs"
					textColor="text.softest"
				>
					{t(isOpen ? 'person_list.view_less' : 'person_list.view_all')}
				</Button>
			)}
		</Flex>
	);
};

export const StyledHeading = (props: HeadingProps) => {
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();

	if (isHomepageRedesignEnabled) {
		return <Heading as="h2" color="text.default" fontSize={100} fontWeight="semibold" lineHeight={6} {...props} />;
	}

	return <Heading as="h2" color="text.softest" fontSize={50} fontWeight="semibold" lineHeight={5} {...props} />;
};
